import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'

import { FullPageSpinner } from '@/features/auth/components/FullPageSpinner'
import { PublicOnly } from '@/features/auth/components/PublicOnly'
import { signupsOpen } from '@/lib/env'
import { PageViewTracker } from '@/components/PageViewTracker'
import { Seo } from '@/components/Seo'
import { ConsentBanner } from '@/features/consent/ConsentBanner'
import Landing from '@/pages/Landing'
import NotFound from '@/pages/NotFound'
import { TabStub } from '@/pages/stubs/TabStub'

/**
 * Route tree with code-split lazy chunks. Everything outside `Landing`
 * and `NotFound` is loaded on demand so the first-paint bundle on the
 * marketing page doesn't carry the entire authenticated app. Each
 * `lazy()` produces its own JS chunk (Vite handles the split via Rollup
 * dynamic-import semantics).
 *
 * AppShell + the auth guard stay eagerly imported because they sit on
 * the critical path for every /app/* render — splitting them would just
 * add a flash of fallback for no gain.
 *
 *   PublicOnly  →  unauthenticated users only; signed-in users → /app
 *   RequireAuth →  authenticated users only; signed-out users → /login
 *   AppShell    →  layout (TopBar + Outlet + BottomNav) for every /app/* page
 */

// Auth surfaces
// Lazy: AppShell reaches the api client and therefore Amplify. Statically
// imported it put the whole auth stack in the entry graph for visitors who
// never sign in.
// Lazy for the same reason as AppShell: RequireAuth reaches the profile hook,
// the api client and Amplify. Only authenticated routes need it.
const RequireAuth = lazy(() =>
  import('@/features/auth/components/RequireAuth').then((m) => ({ default: m.RequireAuth })),
)
const AppShell = lazy(() =>
  import('@/features/app/components/AppShell').then((m) => ({ default: m.AppShell })),
)
const Auth = lazy(() => import('@/pages/Auth'))
const AuthCallback = lazy(() => import('@/pages/AuthCallback'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'))
const DiscordCallback = lazy(() => import('@/pages/DiscordCallback'))
const Onboarding = lazy(() => import('@/pages/Onboarding'))
const NdaRequired = lazy(() => import('@/pages/NdaRequired'))
const SignupsComingSoon = lazy(() => import('@/pages/SignupsComingSoon'))

/**
 * Read once at module load rather than per render. Vite inlines
 * `import.meta.env` at build time, so this is a build-time constant — flipping
 * it means redeploying, which is the intended workflow: the value lives in the
 * GitHub environment, not in anything a request can influence.
 */
const SIGNUPS_OPEN = signupsOpen()

// Public-ish deep links
const InviteLanding = lazy(() => import('@/pages/InviteLanding'))
const EventRedirect = lazy(() => import('@/pages/EventRedirect'))

// Authenticated app surfaces
const Home = lazy(() => import('@/pages/Home'))
const Friends = lazy(() => import('@/pages/Friends'))
const Profile = lazy(() => import('@/pages/Profile'))
const Games = lazy(() => import('@/pages/Games'))
const GameDetail = lazy(() => import('@/pages/GameDetail'))
const Live = lazy(() => import('@/pages/Live'))
const SquadronManage = lazy(() => import('@/pages/SquadronManage'))
const SquadronTiers = lazy(() => import('@/pages/SquadronTiers'))
const SquadronRevenue = lazy(() => import('@/pages/SquadronRevenue'))
const SquadronFeeChanges = lazy(() => import('@/pages/SquadronFeeChanges'))
const SquadronMembership = lazy(() => import('@/pages/SquadronMembership'))
const SquadronMembershipReview = lazy(() => import('@/pages/SquadronMembershipReview'))
const SquadronMembershipCreated = lazy(() => import('@/pages/SquadronMembershipCreated'))
const ActivateMembership = lazy(() => import('@/pages/ActivateMembership'))
const ActivateOrLeave = lazy(() => import('@/pages/ActivateOrLeave'))
const ActivateTier = lazy(() => import('@/pages/ActivateTier'))
const ClaimPrize = lazy(() => import('@/pages/ClaimPrize'))
const CreateEvent = lazy(() => import('@/pages/CreateEvent'))
const EventDetail = lazy(() => import('@/pages/EventDetail'))
const EditEvent = lazy(() => import('@/pages/EditEvent'))
const MyEvents = lazy(() => import('@/pages/MyEvents'))
const EditProfile = lazy(() => import('@/pages/EditProfile'))
const AgeVerify = lazy(() => import('@/pages/AgeVerify'))
const Stadium = lazy(() => import('@/pages/Stadium'))
const StadiumDemo = lazy(() => import('@/pages/StadiumDemo'))
const ExploreSquadrons = lazy(() => import('@/pages/ExploreSquadrons'))
const ExploreTournaments = lazy(() => import('@/pages/ExploreTournaments'))
const ExploreLive = lazy(() => import('@/pages/ExploreLive'))
const ExplorePricing = lazy(() => import('@/pages/ExplorePricing'))
const ChangeEmail = lazy(() => import('@/pages/ChangeEmail'))
const DeleteAccount = lazy(() => import('@/pages/DeleteAccount'))
const NotificationSettings = lazy(() => import('@/pages/NotificationSettings'))
const NotificationsInbox = lazy(() => import('@/pages/NotificationsInbox'))
const Subscription = lazy(() => import('@/pages/Subscription'))
const Bookmarks = lazy(() => import('@/pages/Bookmarks'))
const TournamentStats = lazy(() => import('@/pages/TournamentStats'))

export default function App() {
  return (
    <Suspense fallback={<FullPageSpinner label="loading" />}>
      <Seo />
      <PageViewTracker />
      <ConsentBanner />
      <Routes>
        <Route path="/" element={<Landing />} />

        {/* Deep links — public; redirect into the app post-auth */}
        <Route path="/i/:token" element={<InviteLanding />} />
        <Route path="/e/:eventId" element={<EventRedirect />} />

        {/* Tournament Stadium — full-screen TV view; auth-via-token,
            no Cognito session, no AppShell. The `demo` slug is an
            open marketing demo (no gate — same content for everyone,
            crawlers included) — must be declared BEFORE the
            parameterised :eventId route so it doesn't get swallowed
            as an event ID. */}
        <Route path="/stadium/demo" element={<StadiumDemo />} />
        <Route path="/stadium/:eventId" element={<Stadium />} />

        {/* Public feature-explore pages — see docs/MARKETING_EXPLORE_PLAN.md.
            Each route lives outside the AppShell since they're pre-sign-in
            marketing surfaces with their own nav (the Landing's Nav). */}
        <Route path="/explore/squadrons" element={<ExploreSquadrons />} />
        <Route path="/explore/tournaments" element={<ExploreTournaments />} />
        <Route path="/explore/live" element={<ExploreLive />} />
        <Route path="/pricing" element={<ExplorePricing />} />

        {/* NDA gate landing — shown when alpha pre-check rejects an email */}
        <Route path="/nda-required" element={<NdaRequired />} />

        {/* Auth */}
        {/*
          * Gated on VITE_SIGNUPS_OPEN. This route swap is what actually closes
          * signups — changing the nav buttons alone would still leave the form
          * reachable by typed URL, stale link, or any of the in-page CTAs.
          * /forgot-password, /verify-email and the OAuth callbacks stay open so
          * existing accounts are not locked out of recovery.
          */}
        <Route
          path="/login"
          element={SIGNUPS_OPEN ? <PublicOnly><Auth /></PublicOnly> : <SignupsComingSoon />}
        />
        <Route
          path="/signup"
          element={SIGNUPS_OPEN ? <PublicOnly><Auth /></PublicOnly> : <SignupsComingSoon />}
        />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/discord-callback" element={<DiscordCallback />} />

        {/* Onboarding — handles its own profile-completeness gate */}
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Authenticated app — RequireAuth checks session + profile, AppShell
            wraps every child with the top bar + bottom nav. */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="profile" element={<Profile />} />
          <Route path="friends" element={<Friends />} />
          <Route path="games" element={<Games />} />
          <Route path="games/:gameId" element={<GameDetail />} />
          <Route path="live" element={<Live />} />
          <Route path="squadron" element={<SquadronManage />} />
          <Route path="squadron/tiers" element={<SquadronTiers />} />
          <Route path="squadron/finance/revenue" element={<SquadronRevenue />} />
          <Route path="squadron/finance/fee-changes" element={<SquadronFeeChanges />} />
          <Route path="squadron/finance/membership" element={<SquadronMembership />} />
          <Route path="squadron/finance/membership/review" element={<SquadronMembershipReview />} />
          <Route path="squadron/finance/membership/success" element={<SquadronMembershipCreated />} />
          <Route path="squadron/activate" element={<ActivateMembership />} />
          <Route path="squadron/activate-or-leave" element={<ActivateOrLeave />} />
          <Route path="squadron/activate-tier" element={<ActivateTier />} />
          {/* Phase C.2b — prize claim landing for the tournament-payout
              push deep-link. Routes the winner into Stripe Express
              onboarding so the AWAITING_ONBOARDING row can settle. */}
          <Route path="prize/claim" element={<ClaimPrize />} />
          <Route path="events/create" element={<CreateEvent />} />
          <Route path="events/:eventId" element={<EventDetail />} />
          <Route path="events/:eventId/edit" element={<EditEvent />} />

          {/* Profile sub-routes (Phase F) */}
          <Route path="profile/edit" element={<EditProfile />} />
          <Route path="profile/age-verify" element={<AgeVerify />} />
          <Route path="profile/email" element={<ChangeEmail />} />
          <Route path="profile/delete" element={<DeleteAccount />} />
          <Route path="profile/notifications" element={<NotificationSettings />} />
          <Route path="profile/subscription" element={<Subscription />} />
          <Route path="notifications" element={<NotificationsInbox />} />
          <Route path="profile/bookmarks" element={<Bookmarks />} />
          <Route
            path="profile/tournament-stats/:userId"
            element={<TournamentStats />}
          />
          <Route
            path="profile/tournament-stats"
            element={<TournamentStats />}
          />
          <Route path="profile/events" element={<MyEvents />} />
          <Route
            path="profile/feedback"
            element={
              <TabStub
                label="Send feedback"
                description="Bug report, feature request, anything else. Ships next phase."
                next="Back to profile"
              />
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}
